import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { conflictingSources, needsIntro, needsList, painPoints, searchQuestions, symptoms, systemShows } from '../data/painPoints';
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }
} as const;
export function PainPoints() {
  return <section className="py-24 px-4 bg-mist" data-id="element-170" data-mp="1xwgc6udciflt">
      <div className="max-w-4xl mx-auto" data-id="element-171" data-mp="21zur1hpjspxo">
        <motion.div {...reveal} className="text-center mb-12" data-id="element-172" data-mp="220waoakmqkr0">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-ink uppercase" data-id="element-173" data-mp="1o0da1oihugyv">
            Still Spending Hours Preparing <span className="text-forest" data-id="element-174" data-mp="1sz6lzci4tzwx">Workplace Safety Training</span>{' '}
            Sessions?
          </h2>
          <div className="text-lg text-graphite leading-relaxed bg-white p-6 md:p-8 rounded-2xl border border-line text-left space-y-4" data-id="element-175" data-mp="3ibi7uc6wfz6a">
            <p data-id="element-176" data-mp="1z0exyqe3uy49">
              {conflictingSources.map(line => <React.Fragment key={line} data-id="element-177" data-mp="1yykgxn5yfq5g">
                  {line}
                  <br data-id="element-178" data-mp="1u12f0gqcub6y" />
                </React.Fragment>)}
            </p>
            <p className="text-ink font-semibold" data-id="element-179" data-mp="27rxifdi34035">
              {symptoms.map((line, index) => <React.Fragment key={line} data-id="element-180" data-mp="23somc1zknq4f" data-mpi={index}>
                  {line}
                  {index < symptoms.length - 1 && <br data-id="element-181" data-mp="1g2lwlsj4qw8z" />}
                </React.Fragment>)}
            </p>
            <p className="text-ink font-semibold" data-id="element-182" data-mp="1b3rhb0kdfptm">{needsIntro}</p>
            <p data-id="element-183" data-mp="1p2ha4405r3c6">
              {needsList.map((line, index) => <React.Fragment key={line} data-id="element-184" data-mp="1p3si6g0myx93" data-mpi={index}>
                  {line}
                  {index < needsList.length - 1 && <br data-id="element-185" data-mp="1k5ilg0z9jold" />}
                </React.Fragment>)}
            </p>
            <p data-id="element-186" data-mp="30ft9a1kz2xs0">Suddenly, you&apos;re facing the same questions again:</p>
            <ul className="space-y-2 pl-1" data-id="element-187" data-mp="2vhjevt72o17g">
              {searchQuestions.map(question => <li key={question} className="flex items-start gap-3 text-ink font-semibold" data-id="element-188" data-mp="1t43nz6ygqmyx">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0 mt-3" aria-hidden="true" data-id="element-189" data-mp="1y3gi8crnywbj" />
                  <span data-id="element-190" data-mp="226wh7fr7h8re">{question}</span>
                </li>)}
            </ul>
            <p className="text-forest font-semibold" data-id="element-191" data-mp="27568hf9t7p7q">
              You already know how important quality safety education is.
            </p>
            <p data-id="element-192" data-mp="1ackm53g6deyh">
              But finding, organizing and preparing professional training materials can become a
              second job.
            </p>
            <p data-id="element-193" data-mp="1jf3oznjynrkq">
              And when every safety topic requires a different resource, preparing your next session
              takes much longer than it should.
            </p>
          </div>
        </motion.div>

        <motion.div {...reveal} data-id="element-194" data-mp="qpwpal8rsvxw">
          <h3 className="text-xl font-bold text-ink mb-8 text-center uppercase tracking-wide" data-id="element-195" data-mp="qotyedbg6thz">
            The Real Cost of Training Without a Complete System
          </h3>
          <div className="overflow-hidden rounded-xl border border-line bg-white shadow-sm shadow-ink/5" data-id="element-196" data-mp="o5qb7kqfzp3m">
            {painPoints.map((point, index) => <motion.p key={point} initial={{
            opacity: 0,
            x: -12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.25,
            delay: Math.min(index * 0.04, 0.3),
            ease: [0.23, 1, 0.32, 1]
          }} className="flex items-start gap-3 px-5 py-4 text-graphite text-sm leading-relaxed border-t first:border-t-0 border-line" data-id="element-197" data-mp="e7j55wxoyrge" data-mpi={index}>
                <X size={16} className="text-red-600 shrink-0 mt-0.5" aria-hidden="true" data-id="element-198" data-mp="3rkw2njg3utt3" />
                {point}
              </motion.p>)}
          </div>

          <div className="text-center mt-12 pt-8 border-t border-line" data-id="element-199" data-mp="3ikz85171b0q4">
            <p className="text-2xl font-bold tracking-tight text-ink" data-id="element-200" data-mp="3dmnzuuwe0ya0">
              You don’t need more disconnected safety information.
            </p>
            <p className="text-xl mt-4 text-graphite font-semibold uppercase" data-id="element-201" data-mp="1hm5pc5i269uv">
              You need a complete <span className="text-forest" data-id="element-202" data-mp="1lnqwda7wvtx5">professional system</span> that shows
              you:
            </p>
            <ul className="mt-5 space-y-1.5" data-id="element-203" data-mp="1ctckziqi53jb">
              {systemShows.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-forest" data-id="element-204" data-mp="1cqzswcr15csj">
                  {line}
                </li>)}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>;
}